import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Catches render errors (e.g. bad API data) so the app doesn't show a blank white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message || "Unknown error" };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("SetPilot UI error:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "#0a0a0c",
            color: "#f9fafb",
            fontFamily: "system-ui, sans-serif",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: 20, marginBottom: 12 }}>Something went wrong</h1>
          <p style={{ color: "#9ca3af", maxWidth: 480, marginBottom: 16 }}>
            The UI hit an unexpected error. Try refreshing. If this happened after uploading, tell us
            what you see in the browser console (F12 → Console).
          </p>
          <pre
            style={{
              textAlign: "left",
              fontSize: 12,
              color: "#fca5a5",
              maxWidth: "100%",
              overflow: "auto",
              padding: 12,
              background: "#1a1a22",
              borderRadius: 8,
            }}
          >
            {this.state.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              padding: "10px 20px",
              borderRadius: 8,
              border: "1px solid #f97316",
              background: "rgba(249,115,22,0.15)",
              color: "#fb923c",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
